import type { Language } from '../context/LanguageProvider';
import cs from '../lang/cs.json';
import de from '../lang/de.json';
import en from '../lang/en.json';
import es from '../lang/es.json';
import fr from '../lang/fr.json';
import it from '../lang/it.json';
import nl from '../lang/nl.json';
import pl from '../lang/pl.json';
import ro from '../lang/ro.json';

export type LandingTranslation = typeof en;

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends Array<infer U>
    ? Array<DeepPartial<U>>
    : T[K] extends object
      ? DeepPartial<T[K]>
      : T[K];
};

const translations: Record<Language, DeepPartial<LandingTranslation>> = {
  en,
  cs,
  de,
  fr,
  it,
  pl,
  es,
  ro,
  nl,
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function deepMerge<T>(base: T, override: DeepPartial<T> | undefined): T {
  if (!override) {
    return base;
  }

  if (!isPlainObject(base) || !isPlainObject(override)) {
    return override as T;
  }

  const result: Record<string, unknown> = { ...base };

  for (const [key, value] of Object.entries(override)) {
    if (value === undefined) {
      continue;
    }

    const baseValue = result[key];
    result[key] =
      isPlainObject(baseValue) && isPlainObject(value)
        ? deepMerge(baseValue, value)
        : value;
  }

  return result as T;
}

export function getLandingTranslation(language: Language): LandingTranslation {
  return deepMerge(en, translations[language]);
}
